import React, { useState,useEffect } from 'react';
import './audio.css';
import {doc, getDoc, getFirestore, setDoc} from 'firebase/firestore';
import {  useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  ChevronRight,
  SkipBack,
  Pause,
  SkipForward,
  X,
  Play,
  ChevronLeft
} from "lucide-react"

export default function AudioPlayer() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false);
  const [book, setBook] = React.useState([]);
  const [userId, setUserId] = useState(null); 
  const [progress, setProgress] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [stopAfterSeconds, setStopAfterSeconds] = useState(null);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const timerRef = React.useRef(null);
  const [savedPosition, setSavedPosition] = useState(0);


  const auth = getAuth();
  const database = getFirestore();
  const audioRef = React.useRef(null);
  const { id } = useParams();

  //listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null); // or redirect to login
      }
    });
    return () => unsubscribe();
  }, [auth]);

  //fetch book data from firebase
  useEffect(() => {
    const fetchBook = async () => {
      try {
        const bookRef = doc(database, "books", id);
        const bookSnap = await getDoc(bookRef);
        if (bookSnap.exists()) {
          setBook({ id: bookSnap.id, ...bookSnap.data() });
        } else {
          console.log("No such book!");
        }
      } catch (error) {
        console.error("Error fetching book:", error);
      }
    };
    fetchBook();
  }, [id, database]);
  
  useEffect(() => {
    const fetchUserProgress = async () => {
      try {
        const docRef = doc(database, "timeSaved", userId);
        const docSnap = await getDoc(docRef);
  
        if (docSnap.exists()) {
          const data = docSnap.data();
          setCurrentChapterIndex(data.chapterIndex || 0);
          setSavedPosition(data.position || 0);
          setShowModal(true); // ask user if they want to continue
          console.log("📖 Loaded saved progress:", data);
        }
      } catch (error) {
        console.error("❌ Failed to fetch saved progress:", error);
      }
    };
  
    if (userId) fetchUserProgress();
  }, [userId, database]);
  

  //audio progress
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => {
      setProgress((audio.currentTime / audio.duration) * 100);
    };

    audio.addEventListener('timeupdate', updateProgress);
    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
    };
  }, []);

  // When user moves the slider
  const handleSliderChange = (e) => {
    const audio = audioRef.current;
    const newProgress = parseFloat(e.target.value);
    if (audio.duration) {
      audio.currentTime = (newProgress / 100) * audio.duration;
    }
    setProgress(newProgress);
  };

  // Save progress to Firebase
  useEffect(() => {
    const writeUserData = async (chapterIndex, position, bookTitle) => {
      try {
        await setDoc(doc(database, "timeSaved", userId), {
          bookTitle,
          chapterIndex,
          position, // time in seconds
          updatedAt: Date.now(),
        });
        console.log("✅ Chapter & position saved:", chapterIndex, position);
      } catch (error) {
        console.error("❌ Error saving progress:", error);
      }
    };
  
    if (userId && book?.title && audioRef.current) {
      const audio = audioRef.current;
      const timeout = setTimeout(() => {
        writeUserData(currentChapterIndex, audio.currentTime, book.title);
      }, 10000); // save after 10s
  
      return () => clearTimeout(timeout);
    }
  }, [userId, book.title, currentChapterIndex, audioRef, database]);
  
  
  // continue from the last saved time

  const handleContinue = () => {
    const audio = audioRef.current;
    if (!audio) return;
  
    // Set chapter first
    const chapter = book?.chapters?.[currentChapterIndex];
    if (chapter) {
      audio.pause();
      audio.src = chapter.url;
      audio.load();
  
      audio.onloadeddata = () => {
        audio.currentTime = savedPosition || 0;
        audio.play();
        setIsPlaying(true);
        setShowModal(false);
        console.log(`⏩ Resumed from Chapter ${currentChapterIndex + 1}, time ${savedPosition}s`);
      };
    } else {
      console.log("⚠️ Cannot continue — chapter not found.");
    }
  };
  
  
  const handleSkip = () => {
  setShowModal(false);
  }

// Skip forward 10 seconds
const skipForward = ()=> {
  const audio = audioRef.current;
  if (!audio) return;
  audio.currentTime += 15;
}
// Skip back 10 seconds
const  skipBack = ()=> {
  const audio = audioRef.current;
  if (!audio) return;
  audio.currentTime -= 15;
}

//stop after 30 minutes 
const startAudio = () => {
  const audio = audioRef.current;

  // Clear any previous timers
  clearTimeout(timerRef.current);

  if (stopAfterSeconds) {
    // Set a timeout to stop the audio
    timerRef.current = setTimeout(() => {
      audio.pause();
      setIsPlaying(false);
    }, stopAfterSeconds * 1000);
  }
};


useEffect(() => {
  // Clean up the timer if the component unmounts
  return () => clearTimeout(timerRef.current);
}, []);


  

  //user clicks play
  const togglePause = () => {
    const audio = audioRef
    if (!audio) return;

    if (isPlaying) {
      audio.current.pause();
    } else {
      audio.current.play();
      clearTimeout(timerRef.current);
    }

    setIsPlaying(!isPlaying);
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  
  


  const chapterElements = book?.chapters?.map((chapter, index) => {
    const handleChapterClick = () => {
      const audio = audioRef.current;
      if (!audio) return;
  
      audio.pause();
      audio.src = chapter.url;
      audio.load();
  
      audio.onloadeddata = () => {
        audio.play();
        setIsPlaying(true);
      };
  
      setCurrentChapterIndex(index);
    };
  
    const isActive = index === currentChapterIndex;
  
    // Extract clean chapter name (remove number prefix and extension)
    const displayName = chapter.name
      ?.replace(/^\d+\.\s*/, "") // remove "1." or "1. "
      ?.replace(/\.mp3$/i, "");  // remove ".mp3"
  
    return (
      <div
        className={`chapter ${isActive ? "active-chapter" : ""}`}
        key={index}
        onClick={handleChapterClick}
      >
        <span className="chapter-number">{index + 1}</span>
        <div className="chapter-info">
          <h3>{displayName || `Chapter ${index + 1}`}</h3>
          <p>{isActive ? "Now Playing" : "Play"}</p>
        </div>
      </div>
    );
  });
  
  

  return (
    <div className='audio-body'>
            <div className="audio-player">
      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <button className="close-btn" onClick={toggleSidebar}>
            <X size={24} />
          </button>
          <h2>Chapters</h2>
        </div>
        <div className="chapters-list"> 
          {chapterElements}

        </div>
      </div>

      {/* Main Player */}
      {showModal && (
  <div className="modal-backdrop">
    <div className="modal">
      <h2>Continue where you left off, princess?</h2>
      <p>
        You were on <strong>Chapter {currentChapterIndex + 1}</strong>
        {savedPosition > 0 && (
          <>
            , at <strong>{Math.floor(savedPosition / 60)}m {Math.floor(savedPosition % 60)}s</strong>
          </>
        )}
      </p>
      <button onClick={handleContinue}>Yes</button>
      <button onClick={handleSkip}>No</button>
    </div>
  </div>
)}


      <div className="player-container">
        

        <div className="player-header">
          <button className="down-btn" onClick={toggleSidebar}>
           {sidebarOpen ? <ChevronRight size={28} />: <ChevronLeft size={28} />}
          </button>
          <div className="title">{book.title}</div>
          <button className="more-btn">
          </button>
        </div>

        <div className="cover-art">
          <img src={book.bookimg}alt="Book Cover" />
        </div>

        <div className="track-info">
        <h1 className="track-title">
  {book?.chapters?.[currentChapterIndex]?.name
    ?.replace(/^\d+\.\s*/, "")
    ?.replace(/\.mp3$/i, "")}
</h1>
          <div className="track-author">Narrated by Nicky</div>
          <div className="liked">
          </div>
        </div>

        <div className="progress-container">
        <input
  type="range"
  min="0"
  max="100"
  value={progress}
  onChange={handleSliderChange}
  style={{
    '--progress': `${progress}%`,
  }}
  className="progress-bar"
/>

<audio
  ref={audioRef}
  src={
    book?.chapters?.length > 0 && currentChapterIndex !== null
      ? book.chapters[currentChapterIndex].url
      : ''
  }
  onEnded={() => {
    if (currentChapterIndex < book.chapters.length - 1) {
      setCurrentChapterIndex(prev => prev + 1);
    }
  }}
>
  Your browser does not support the audio element
</audio>

      </div>

        <div className="controls">
          <button className="control-btn shuffle">
         
          </button>
          <button className="control-btn prev" onClick={skipBack}>
            <SkipBack size={28} />
          </button>
          <button className="control-btn play" onClick={()=>{togglePause(); startAudio(); setStopAfterSeconds(1800)}}>
            {isPlaying ? <Pause size={28} />: <Play size={28} /> }
          </button>
          <button className="control-btn next" onClick={skipForward}>
            <SkipForward size={28} />
          </button>
          <button className="control-btn repeat">
         
          </button>
        </div>

       
      </div>
    </div>
    </div>
    
  )
}
